/**
 * ACH Payouts — the ops screen for paying bank-transfer refunds.
 *
 * The flow, and why it is three steps rather than one:
 *
 *   1. DOWNLOAD  builds a Chase-format CSV from the refunds waiting to be paid
 *                and claims them, so a second download cannot pay them twice.
 *   2. UPLOAD    happens in Chase, not here. Nothing in Ronin can send money.
 *   3. MARK SENT is the person saying the bank has the file. Only then are the
 *                refunds recorded as paid and the stored bank details deleted.
 *
 * If step 2 fails, Cancel puts every refund in that file back in the queue.
 *
 * The file holds real bank details, so it is handed straight to the browser as
 * a download and never stored anywhere else.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import moment from "moment";
import { toast } from "react-hot-toast";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import ConfirmDialog from "../../components/ConfirmDialog";
import {
  AchBatch,
  AchPending,
  AchRefund,
  cancelAchBatch,
  confirmAchBatch,
  downloadCsv,
  exportAchBatch,
  loadAchPending,
} from "./achApi";
import { RefundEvent, money } from "./rules";

const BATCH_STATUS_LABEL: Record<AchBatch["status"], string> = {
  draft: "Downloaded — not yet sent",
  sent: "Sent to the bank",
  canceled: "Canceled",
};

const BATCH_STATUS_CLASS: Record<AchBatch["status"], string> = {
  draft: "bg-amber-100 text-amber-900",
  sent: "bg-green-100 text-green-800",
  canceled: "bg-gray-100 text-gray-600",
};

export function AchPayoutsPanel({ event }: { event: RefundEvent }) {
  const [data, setData] = useState<AchPending | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<AchBatch | null>(null);
  const [canceling, setCanceling] = useState<AchBatch | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await loadAchPending(event.id));
    } catch (err: any) {
      setError(err?.message || "Unable to load bank-transfer refunds");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const refundColumns: ColDef<AchRefund>[] = useMemo(
    () => [
      { headerName: "Wristband", colId: "uid", valueGetter: (p) => p.data?.uid, width: 170 },
      {
        headerName: "Name",
        colId: "name",
        valueGetter: (p) => p.data?.recipient_name ?? "",
        flex: 1,
        minWidth: 160,
      },
      {
        headerName: "Email",
        colId: "email",
        valueGetter: (p) => p.data?.recipient_email ?? "",
        flex: 1,
        minWidth: 180,
      },
      {
        headerName: "Account",
        colId: "account",
        valueGetter: (p) =>
          p.data?.bank_account_last4 ? `••••${p.data.bank_account_last4}` : "—",
        width: 120,
      },
      {
        headerName: "Type",
        colId: "type",
        valueGetter: (p) => p.data?.bank_account_type ?? "",
        width: 110,
      },
      {
        headerName: "Amount",
        colId: "amount",
        valueGetter: (p) => p.data?.amount ?? 0,
        valueFormatter: (p) => money(p.value),
        width: 120,
      },
      {
        headerName: "Requested",
        colId: "created",
        valueGetter: (p) => (p.data?.created_at ? new Date(p.data.created_at) : null),
        valueFormatter: (p) => (p.value ? moment(p.value).format("MMM D, YYYY h:mm A") : "—"),
        width: 190,
      },
    ],
    []
  );

  const download = async () => {
    setBusy(true);
    try {
      const result = await exportAchBatch(event.id);
      downloadCsv(result.fileName, result.csv);
      toast.success(
        `${result.count} refund${result.count === 1 ? "" : "s"} (${money(result.totalAmount)}) — upload the file to Chase, then mark it sent.`
      );
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || "Could not build the payment file");
    } finally {
      setBusy(false);
    }
  };

  const markSent = async (batch: AchBatch) => {
    setBusy(true);
    try {
      const result = await confirmAchBatch(batch.guid);
      toast.success(`${result.marked} refund${result.marked === 1 ? "" : "s"} marked as paid.`);
      setConfirming(null);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || "Could not mark that file as sent");
    } finally {
      setBusy(false);
    }
  };

  const putBack = async (batch: AchBatch) => {
    setBusy(true);
    try {
      const result = await cancelAchBatch(batch.guid);
      toast.success(
        `${result.released} refund${result.released === 1 ? "" : "s"} back in the queue.`
      );
      setCanceling(null);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || "Could not cancel that file");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="mt-6 text-sm text-gray-500">Loading bank transfers…</p>;
  if (error) {
    return (
      <div className="mt-6 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">
        {error}
        <button className="ml-3 font-medium underline" onClick={refresh}>
          Try again
        </button>
      </div>
    );
  }
  if (!data) return null;

  const drafts = data.batches.filter((b) => b.status === "draft");

  return (
    <div className="mt-6 space-y-6">
      {data.problems.length > 0 && (
        <div className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <p className="font-medium">This event cannot pay refunds by bank transfer yet:</p>
          <ul className="mt-1 list-disc pl-5">
            {data.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {/* What the next file will say, so nobody has to open the CSV to check. */}
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-white p-4 text-sm sm:grid-cols-5">
        <div>
          <p className="text-xs text-gray-500">Waiting to be paid</p>
          <p className="text-lg font-semibold text-gray-900">{data.refunds.length}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-lg font-semibold text-gray-900">{money(data.totalAmount)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Pay from</p>
          <p className="font-medium text-gray-900">
            {data.settings.accountLast4 ? `Chase ••••${data.settings.accountLast4}` : "not set"}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Statement shows</p>
          <p className="font-medium text-gray-900">
            {data.settings.entryDescription} ({data.settings.secCode})
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Arrives</p>
          <p className="font-medium text-gray-900">
            {moment(data.settings.nextEffectiveDate).format("ddd MMM D")}
          </p>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-800">Waiting to be paid</h2>
          {/* Excel silently rewrites this file: leading zeros vanish and long
              account numbers become 1.23E+08. Saving from Excel would send the
              bank those values, so the warning belongs next to the button. */}
          <p className="mt-1 max-w-xl text-xs text-gray-600">
            Upload the file to Chase exactly as downloaded.{" "}
            <strong>Do not open it in Excel</strong> — Excel drops the leading zero from
            routing numbers and turns account numbers into 1.23E+08, and saving from Excel
            would send the bank those values. Use Notepad to look at it.
          </p>
        </div>
        <PrimaryButton
          isDisabled={busy || data.refunds.length === 0 || data.problems.length > 0}
          isLoading={busy}
          onClick={download}
        >
          Download payment file ({data.refunds.length})
        </PrimaryButton>
      </div>

      <div className="ag-theme-quartz" style={{ height: 380, width: "100%" }}>
        <AgGridReact<AchRefund>
          rowData={data.refunds}
          columnDefs={refundColumns}
          getRowId={(p) => p.data.guid}
          // Every header sorts — house convention.
          defaultColDef={{ sortable: true, filter: true, resizable: true }}
        />
      </div>

      {drafts.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-900">
            Downloaded, waiting on you
          </h3>
          <p className="mt-1 text-xs text-amber-900">
            Upload the file in Chase (Pay &amp; transfer → ACH Payment Services), then mark
            it sent here. Until you do, these refunds are not counted as paid.
          </p>
          <ul className="mt-3 space-y-2">
            {drafts.map((batch) => (
              <li
                key={batch.guid}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-white px-3 py-2 text-sm"
              >
                <span>
                  <span className="font-medium">{batch.file_name}</span>
                  <span className="ml-2 text-gray-600">
                    {batch.transaction_count} payment
                    {batch.transaction_count === 1 ? "" : "s"} · {money(batch.total_amount)} ·
                    arrives {moment(batch.effective_date).format("MMM D")}
                  </span>
                </span>
                <span className="flex gap-2">
                  <PrimaryButton isDisabled={busy} onClick={() => setConfirming(batch)}>
                    Mark as sent
                  </PrimaryButton>
                  <SecondaryButton isDisabled={busy} onClick={() => setCanceling(batch)}>
                    Cancel
                  </SecondaryButton>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h3 className="text-sm font-semibold text-gray-800">Payment files</h3>
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500">
              <th className="py-1 font-medium">File</th>
              <th className="py-1 font-medium">Payments</th>
              <th className="py-1 font-medium">Total</th>
              <th className="py-1 font-medium">Arrives</th>
              <th className="py-1 font-medium">Status</th>
              <th className="py-1 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {data.batches.length === 0 && (
              <tr>
                <td className="py-2 text-gray-500" colSpan={6}>
                  No payment files yet.
                </td>
              </tr>
            )}
            {data.batches.map((batch) => (
              <tr key={batch.guid} className="border-t border-gray-100">
                <td className="py-1 font-mono text-xs">{batch.file_name}</td>
                <td className="py-1">{batch.transaction_count}</td>
                <td className="py-1">{money(batch.total_amount)}</td>
                <td className="py-1">{moment(batch.effective_date).format("MMM D, YYYY")}</td>
                <td className="py-1">
                  <span
                    className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${BATCH_STATUS_CLASS[batch.status]}`}
                  >
                    {BATCH_STATUS_LABEL[batch.status]}
                  </span>
                </td>
                <td className="py-1">{moment(batch.created_at).format("MMM D, YYYY h:mm A")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!confirming}
        title="Mark this file as sent?"
        busy={busy}
        confirmLabel="Yes, the bank has it"
        cancelLabel="Not yet"
        message={
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Only do this once <strong>{confirming?.file_name}</strong> has been uploaded to
              Chase. It marks {confirming?.transaction_count} refund
              {confirming?.transaction_count === 1 ? "" : "s"} ({money(confirming?.total_amount || 0)})
              as paid.
            </p>
            <p>
              The attendees' bank details are deleted at the same time, so the file cannot be
              rebuilt afterwards. Keep your copy until the payments have cleared.
            </p>
          </div>
        }
        onConfirm={() => confirming && markSent(confirming)}
        onCancel={() => setConfirming(null)}
      />

      <ConfirmDialog
        open={!!canceling}
        title="Cancel this file?"
        busy={busy}
        confirmLabel="Yes, put them back"
        cancelLabel="Keep it"
        message={
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              Every refund in <strong>{canceling?.file_name}</strong> goes back in the queue
              and can be exported again.
            </p>
            <p>
              Only do this if the file was <strong>not</strong> uploaded to Chase. If it was,
              canceling here would pay those people twice on the next file.
            </p>
          </div>
        }
        onConfirm={() => canceling && putBack(canceling)}
        onCancel={() => setCanceling(null)}
      />
    </div>
  );
}

export default AchPayoutsPanel;
